import React from 'react';
import { SelectMenu, type DateRange } from '../../../ui-kit';
import { fetchReportDashboard } from '../../../services/manualDashboardService';
import type { ReportPayload } from '../../../types/reportDashboard';

export interface ReportQuery {
  dashboardId?: string;
  range: DateRange;
  filters: Record<string, string[]>;
  /** Bumped when the report is refreshed from the warehouse. */
  refreshNonce: number;
}

/**
 * One table's or chart's own cohort grouping, as every board table has.
 *
 * Follows the report's grouping until the reader picks another; then the
 * report is fetched at that grouping for this section alone (same range, same
 * filters, from the cached rows). The choice resets when the range changes,
 * because a new range offers different groupings.
 */
export function useReportCohort(payload: ReportPayload, { dashboardId, range, filters, refreshNonce }: ReportQuery) {
  const [chosen, setChosen] = React.useState<string | undefined>(undefined);
  React.useEffect(() => setChosen(undefined), [range]);

  const options = (payload.available_grains ?? []).map((g) => ({
    value: g.key,
    label: g.label,
    description: `${g.periods} cohorts${g.key === payload.grain ? ' · the default for this range' : ''}`,
  }));
  const grain = chosen && options.some((o) => o.value === chosen) ? chosen : payload.grain;
  const active = grain !== payload.grain;

  const [regrouped, setRegrouped] = React.useState<ReportPayload | null>(null);
  const [loading, setLoading] = React.useState(false);
  const filtersKey = JSON.stringify(filters);
  React.useEffect(() => {
    if (!active || !dashboardId) {
      setRegrouped(null);
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    fetchReportDashboard(
      dashboardId,
      { filters: JSON.parse(filtersKey), start: range.start, end: range.end, grain },
      controller.signal,
    )
      .then(setRegrouped)
      .catch(() => setRegrouped(null))
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [active, dashboardId, grain, range.start, range.end, filtersKey, refreshNonce]);

  const control = options.length > 1 ? (
    <SelectMenu label="Cohort" value={grain} options={options} onChange={setChosen} />
  ) : null;

  return {
    /** The report to read this section from: the page's own, or regrouped. */
    source: active && regrouped ? regrouped : payload,
    grain,
    control,
    regrouping: active && loading,
  };
}
